/**
 * Free Gift（分档 · 全程虚拟占位，真实加购由 checkout extension 完成）
 * —— dreame-belgium 主题适配版（自 dreame-german 版迁移，源自 MEET/Horizon 版，无模块依赖）。
 *
 * - 资格小计 = 非赠品行 original_line_price 之和（分）→ 命中档位。
 * - 赠品行用 `_free_gift` line property 识别（与 function / extension 同口径）。
 *   赠品商品允许顾客正常购买：正常购买行不带该标记，不会被清理，且计入资格小计。
 * - 档1（≥2 个有货候选）：在赠品行放下拉选择框，可切换有库存的赠品；单候选不显示。
 *
 * 为避免频繁写购物车触发 429，店内（购物车页 + ajax 抽屉）一律只注入虚拟赠品行，
 * 不真实加购。本文件只负责：(1) 展示/移除虚拟行；(2) 把候选优先级列表写进
 * cart attribute `_free_gift_variant`（档位/选择/库存变化时才写，频率极低）；
 * (3) 清掉购物车里残留的带标记真实赠品行（如从 checkout 弃单返回）。
 * 真实加购发生在 checkout 内：free-gift-checkout extension 读 attribute 加购
 * （带 _free_gift property），discount function 负责打 0，是免单的最终权威。
 *
 * 触发机制（本主题无统一购物车事件，多源监听）：
 * - 页面加载 / bfcache 恢复（pageshow，强制刷新候选库存）
 * - jQuery `ajaxCart.afterCartLoad`（抽屉每次重建后由 theme.js 触发）
 * - fetch / XHR 拦截：任何脚本请求 /cart/add|change|update|clear 后 reconcile
 * - MutationObserver：抽屉(#CartContainer)与购物车页(.cart.cart-form)重渲染后补回虚拟行
 *
 * 写操作经 #safeFetch，遇 429 退避 30s。加速结账按钮需商家隐藏（extension 在
 * 加速结账流程中无法加购）。若启用本功能，请关闭旧版阶梯满赠（enable_step_free_gift）。
 */
(function () {
  'use strict';

  const VIRTUAL_ATTR = 'data-free-gift-virtual';
  const SELECT_ATTR = 'data-free-gift-select';
  const SELECTED_STORAGE_KEY = 'free-gift:selected-variant';
  const VARIANT_ATTRIBUTE_KEY = '_free_gift_variant';
  const PD_CODE_ATTRIBUTE_KEY = 'pd_code';
  const GIFT_PROPERTY_KEY = '_free_gift';
  const PRODUCT_CACHE_KEY = 'free-gift:product-cache:v2';
  const PRODUCT_CACHE_TTL_MS = 5 * 60 * 1000;
  const DEBOUNCE_MS = 250;
  const THROTTLE_BACKOFF_MS = 30000;
  const CART_MUTATION_RE = /\/cart\/(add|change|update|clear)/;

  function debounce(fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function jsonPost(body) {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
    };
  }

  class FreeGiftManager {
    /** @type {Object | null} */
    #cart = null;
    #throttledUntil = 0;
    /** 自身写购物车期间置真，让 fetch/XHR 拦截忽略自己的请求 */
    #selfWrite = false;
    /** @type {() => void} */
    #refreshDebounced;
    /** @type {() => void} */
    #applyDebounced;
    /** @type {MutationObserver|null} */
    #observer = null;
    /** 正在写 DOM（让 observer 忽略自己引起的变动） */
    #applying = false;
    #ajaxCartBound = false;

    /** 活动截止时间（ms），null = 不限 */
    #endTime = null;
    /** @type {string | null} */
    #selectedVariantId = null;
    /** @type {any} */
    #currentTier = null;
    /** @type {any} */
    #pendingGift = null;
    /** attribute 写入串行化链 */
    #attrSyncChain = Promise.resolve();

    /** @param {Object} config */
    constructor(config) {
      this.giftLabel = config.giftLabel || 'Free gift';
      this.moneyFormat = this.#resolveMoneyFormat(config);
      this.#endTime = config.endTime ? new Date(config.endTime).getTime() : null;
      // 档1 全部售罄时写到订单的标记值（metafield 顶层 pd_code 可配置）
      this.pdCodeValue = String(config.pdCode || 'true');
      this.routes = Object.assign(
        { root: '/', cart: '/cart', cartChange: '/cart/change', cartUpdate: '/cart/update' },
        config.routes || {}
      );
      // 候选来自 shop metafield（经 liquid 透传），只有 sku/variant_id/order；
      // 标题/图片/价格/库存由 #hydrateTier 在浏览器内按 SKU 补全
      this.tiers = (Array.isArray(config.tiers) ? config.tiers : []).map((t) => ({
        level: Number(t.level ?? 0) || 0,
        min: Number(t.minSubtotal ?? t.min),
        max: (t.maxSubtotal ?? t.max) == null ? Infinity : Number(t.maxSubtotal ?? t.max),
        candidates: (Array.isArray(t.candidates) ? t.candidates : [])
          .map((c) => ({
            sku: String(c.sku ?? ''),
            variantId: String(c.variantId ?? c.variant_id ?? ''),
            order: Number(c.order ?? 0) || 0,
            title: '',
            image: '',
            price: null,
            available: true,
            hydrated: false,
          }))
          .filter((c) => c.sku && c.variantId)
          .sort((a, b) => a.order - b.order),
      }));
      // 恢复本标签页内的赠品选择（去 checkout 再返回是整页重载，内存状态会丢）
      try {
        this.#selectedVariantId = sessionStorage.getItem(SELECTED_STORAGE_KEY);
      } catch (error) {
        /* sessionStorage 不可用时退回默认候选 */
      }
      this.#refreshDebounced = debounce(() => this.#reconcile(), DEBOUNCE_MS);
      this.#applyDebounced = debounce(() => this.#applyDom(), 60);
    }

    init() {
      if (this.#isExpired()) {
        this.#removeVirtualRows();
        this.#clearGiftAttribute();
        return;
      }
      // observer 必须在 patchFetch/patchXhr 之前初始化，防止 fetch 拦截触发的
      // 异步 reconcile 在 observer 赋值前调用 #applyDom() 而提前返回
      this.#observer = new MutationObserver(() => {
        if (this.#applying) return;
        this.#applyDebounced();
      });
      document.addEventListener('change', this.#onGiftSelectChange);
      this.#bindAjaxCartEvent();
      this.#patchFetch();
      this.#patchXhr();

      // 从 /checkout 返回（bfcache 恢复）时：先强制刷新当前档候选库存（快照里的
      // available 可能已过期，典型场景是赠品在 checkout 里加购时发现售罄），
      // 再 reconcile 换品/移除虚拟行、纠正 attribute、清掉残留真实赠品行
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        this.#hydrateTier(this.#currentTier, true).finally(() => this.#refreshDebounced());
      });

      this.#reconcile();
    }

    // --- 购物车变化的多源监听 ------------------------------------------------

    /** theme.js 的 ajaxCart 在抽屉每次重建后触发 jQuery 事件 ajaxCart.afterCartLoad */
    #bindAjaxCartEvent() {
      const tryBind = () => {
        if (this.#ajaxCartBound) return;
        if (typeof window.jQuery !== 'function') return;
        this.#ajaxCartBound = true;
        window.jQuery(document.body).on('ajaxCart.afterCartLoad', () => {
          this.#refreshDebounced();
        });
      };
      tryBind();
      if (!this.#ajaxCartBound) {
        document.addEventListener('DOMContentLoaded', tryBind);
        window.addEventListener('load', tryBind);
      }
    }

    /** 任何脚本通过 fetch 改购物车（如 Vue/自定义加购组件）后 reconcile */
    #patchFetch() {
      const orig = window.fetch;
      if (typeof orig !== 'function' || orig.__freeGiftPatched) return;
      const self = this;
      const patched = function (...args) {
        const input = args[0];
        const url = String(input && input.url ? input.url : input || '');
        const promise = orig.apply(this, args);
        if (CART_MUTATION_RE.test(url) && !self.#selfWrite) {
          promise.then(() => self.#refreshDebounced()).catch(() => {});
        }
        return promise;
      };
      patched.__freeGiftPatched = true;
      window.fetch = patched;
    }

    /** jQuery $.ajax 走 XHR（如 unifyAutoUpdateCart 的 /cart/update.js），一并拦截 */
    #patchXhr() {
      const proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
      if (!proto || proto.open.__freeGiftPatched) return;
      const origOpen = proto.open;
      const self = this;
      const patchedOpen = function (method, url, ...rest) {
        if (typeof url === 'string' && CART_MUTATION_RE.test(url) && !self.#selfWrite) {
          this.addEventListener('loadend', () => self.#refreshDebounced());
        }
        return origOpen.call(this, method, url, ...rest);
      };
      patchedOpen.__freeGiftPatched = true;
      proto.open = patchedOpen;
    }

    // --- 状态：算档位 + 清理真实赠品行 ----------------------------------------

    async #reconcile() {
      if (this.#isExpired()) {
        this.#removeVirtualRows();
        await this.#clearGiftAttribute();
        return;
      }
      const cart = await this.#getCart();
      if (!cart || !Array.isArray(cart.items)) return;
      this.#cart = cart;

      const subtotal = this.#qualifyingSubtotal(cart);
      const tier = this.#matchTier(subtotal);
      this.#currentTier = tier;

      // 店内不留带赠品标记的真实行（checkout extension 加购后弃单返回、或历史残留）；
      // 不带标记的同款商品 = 顾客正常购买，不碰
      const realGiftLines = cart.items.filter((i) => this.#hasGiftProperty(i));
      if (realGiftLines.length > 0) {
        const removed = await this.#removeLines(realGiftLines);
        if (removed) this.#refreshThemeCartUI();
      }

      if (tier) await this.#hydrateTier(tier);
      this.#pendingGift = tier ? this.#chooseGift(tier) : null;
      this.#applyDom();
      await this.#syncGiftAttribute();
    }

    /**
     * 把待送赠品同步到 cart attribute（checkout extension 据此加购）。
     * 值为「按优先级排列的有货候选变体 id」逗号列表，首个 = 用户所选；extension
     * 加购失败（如刚好售罄）时按序降级到下一个候选。
     * 仅在值变化时写（档位切换/用户换选/库存变化/失去资格），频率极低。
     */
    #syncGiftAttribute() {
      this.#attrSyncChain = this.#attrSyncChain.then(() => this.#doSyncGiftAttribute()).catch(() => {});
      return this.#attrSyncChain;
    }

    async #doSyncGiftAttribute() {
      const tier = this.#currentTier;
      const chosen = this.#pendingGift;
      let desired = '';
      if (tier && chosen) {
        const fallbacks = this.#availableCandidates(tier)
          .map((c) => c.variantId)
          .filter((id) => id !== chosen.variantId);
        desired = [chosen.variantId, ...fallbacks].join(',');
      }
      // 命中档1 即默认打 pd_code（店内只有虚拟行，赠品尚未真正送达）；
      // 赠品在 checkout 成功送出后由 extension 清除，失去档1资格在这里清除。
      // 这样无论哪个环节失败（售罄/加速结账绕过/加购失败），没赠品的订单必带标记
      const desiredPdCode = tier && tier.level === 1 ? this.pdCodeValue : '';

      const attributes = {};
      const cartAttrs = (this.#cart && this.#cart.attributes) || {};
      const currentVariant = String(cartAttrs[VARIANT_ATTRIBUTE_KEY] ?? '');
      if (currentVariant !== desired) attributes[VARIANT_ATTRIBUTE_KEY] = desired;
      const currentPdCode = String(cartAttrs[PD_CODE_ATTRIBUTE_KEY] ?? '');
      if (currentPdCode !== desiredPdCode) attributes[PD_CODE_ATTRIBUTE_KEY] = desiredPdCode;
      if (Object.keys(attributes).length === 0) return;

      const body = JSON.stringify({ attributes });
      const res = await this.#safeFetch(this.routes.cartUpdate, jsonPost(body));
      if (res && res.ok && this.#cart) {
        this.#cart.attributes = Object.assign({}, this.#cart.attributes || {}, attributes);
      }
    }

    /** 活动截止后清空 _free_gift_variant attribute，让 checkout extension 不再加购 */
    async #clearGiftAttribute() {
      const cart = this.#cart || (await this.#getCart());
      if (!cart) return;
      const current = String((cart.attributes && cart.attributes[VARIANT_ATTRIBUTE_KEY]) ?? '');
      if (!current) return;
      const body = JSON.stringify({ attributes: { [VARIANT_ATTRIBUTE_KEY]: '' } });
      const res = await this.#safeFetch(this.routes.cartUpdate, jsonPost(body));
      if (res && res.ok && this.#cart) {
        this.#cart.attributes = Object.assign({}, this.#cart.attributes, { [VARIANT_ATTRIBUTE_KEY]: '' });
      }
    }

    // --- 候选数据水合（标题/图片/价格/库存，全部只读请求） --------------------

    #readProductCache() {
      try {
        return JSON.parse(sessionStorage.getItem(PRODUCT_CACHE_KEY)) || {};
      } catch (error) {
        return {};
      }
    }

    #writeProductCache(cache) {
      try {
        sessionStorage.setItem(PRODUCT_CACHE_KEY, JSON.stringify(cache));
      } catch (error) {
        /* 存不进就每次现拉 */
      }
    }

    #resolveMoneyFormat(config) {
      const fromConfig = config && config.moneyFormat;
      const fromTheme =
        window.theme &&
        window.theme.strings &&
        window.theme.strings.moneyFormat;
      return String(fromConfig || fromTheme || '${{amount}}');
    }

    /** money 格式化（对齐 theme.Currency / section-common-featured-products） */
    #formatMoney(cents) {
      let amount = cents;
      if (typeof amount === 'string') amount = amount.replace('.', '');
      amount = Number(amount);
      if (!Number.isFinite(amount)) return '';

      const formatString = this.#resolveMoneyFormat({ moneyFormat: this.moneyFormat });
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = formatString.match(placeholderRegex);
      if (!match) return formatString;

      const formatWithDelimiters = (number, precision, thousands = ',', decimal = '.') => {
        const fixed = (number / 100).toFixed(precision);
        const parts = fixed.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
        return parts[1] ? dollars + decimal + parts[1] : dollars;
      };

      let value;
      switch (match[1]) {
        case 'amount':
          value = formatWithDelimiters(amount, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(amount, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(amount, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(amount, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(amount, 0, ' ');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(amount, 2, "'");
          break;
        case 'amount_with_space_separator':
          value = formatWithDelimiters(amount, 2, ' ', ',');
          break;
        default:
          value = formatWithDelimiters(amount, 2);
      }

      const formatted = formatString.replace(placeholderRegex, value);
      return /\{\{/.test(formatted) ? '' : formatted;
    }

    /** 渲染前始终用分（cents）现算，避免 sessionStorage 缓存旧占位符 */
    #giftPriceLabel(gift) {
      if (!gift || gift.price == null || gift.price === '') return '';
      return this.#formatMoney(gift.price);
    }

    /**
     * 按 SKU 解析产品展示数据。
     * 优先按店内约定 handle == SKU 小写直连 /products/<handle>.js —— 赠品占位
     * 商品通常对搜索隐藏，predictive search 查不到，但已发布商品该接口始终可达；
     * 直连不中（handle != SKU 的正常商品）再用 predictive search 解析 handle。
     */
    async #fetchCandidateData(candidate) {
      const root = this.routes.root || '/';
      const directHandle = candidate.sku.toLowerCase();
      const direct = await this.#fetchProductByHandle(root, directHandle, candidate);
      if (direct) return direct;

      const handle = await this.#searchHandleBySku(root, candidate.sku);
      if (!handle || handle === directHandle) return null;
      return this.#fetchProductByHandle(root, handle, candidate);
    }

    async #fetchProductByHandle(root, handle, candidate) {
      let res;
      try {
        res = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' },
        });
      } catch (error) {
        return null;
      }
      if (!res.ok) return null;
      const product = await res.json().catch(() => null);
      const variant = ((product && product.variants) || []).find((v) => String(v.id) === candidate.variantId);
      if (!variant) return null;
      return {
        title: product.title || candidate.sku,
        image: (variant.featured_image && variant.featured_image.src) || product.featured_image || '',
        price: variant.price,
        available: Boolean(variant.available),
      };
    }

    async #searchHandleBySku(root, sku) {
      const url =
        `${root}search/suggest.json?q=${encodeURIComponent(sku)}` +
        `&resources[type]=product&resources[limit]=1` +
        `&resources[options][fields]=variants.sku&resources[options][unavailable_products]=show`;
      let res;
      try {
        res = await fetch(url, { headers: { Accept: 'application/json' } });
      } catch (error) {
        return null;
      }
      if (!res.ok) return null;
      const json = await res.json().catch(() => null);
      const products = json && json.resources && json.resources.results && json.resources.results.products;
      return (products && products[0] && products[0].handle) || null;
    }

    /**
     * 水合某档候选（带 sessionStorage 缓存，TTL 5 分钟）。
     * @param {any} tier
     * @param {boolean} [force] 绕过缓存强制刷新（bfcache 返回时刷库存用）
     */
    async #hydrateTier(tier, force = false) {
      if (!tier || !Array.isArray(tier.candidates)) return;
      const cache = this.#readProductCache();
      const now = Date.now();
      let cacheDirty = false;
      await Promise.all(
        tier.candidates.map(async (candidate) => {
          if (candidate.hydrated && !force) return;
          const hit = cache[candidate.sku];
          if (!force && hit && now - hit.ts < PRODUCT_CACHE_TTL_MS && hit.data && hit.data.price != null) {
            Object.assign(candidate, hit.data, { hydrated: true });
            return;
          }
          const data = await this.#fetchCandidateData(candidate).catch(() => null);
          if (data) {
            Object.assign(candidate, data, { hydrated: true });
            cache[candidate.sku] = { ts: now, data };
            cacheDirty = true;
          } else {
            // 查不到（下架/搜索未命中/网络失败）：隐藏赠品
               Object.assign(candidate, { available: false, hydrated: true });
          }
        })
      );
      if (cacheDirty) this.#writeProductCache(cache);
    }

    // --- DOM 应用（幂等，无网络） ---------------------------------------------

    #applyDom() {
      if (!this.#observer) return;
      this.#applying = true;
      this.#observer.disconnect();
      try {
        // 带标记的真实赠品行永不展示：API 删除/主题重渲染未跟上时，先从 DOM 剔掉
        this.#removeRealGiftRows();
        const tier = this.#currentTier;
        if (!tier) {
          this.#removeVirtualRows();
        } else {
          this.#renderVirtual(this.#pendingGift, tier);
        }
      } finally {
        this.#observeCartContainers();
        // 等本次 DOM 变动的回调被吞掉后再解除标记
        requestAnimationFrame(() => {
          this.#applying = false;
          // 自检：标记期间外部重渲染（如 main-cart 加载后整体替换 .cart.cart-form
          // 的 innerHTML）产生的 mutation 会被上面的屏蔽吞掉，虚拟行可能已被抹掉
          // 且不会再有补回时机；发现容器在而该有的行不在，就补一次
          if (
            this.#currentTier &&
            this.#pendingGift &&
            !document.querySelector(`[${VIRTUAL_ATTR}]`) &&
            document.querySelector('.cart.cart-form .cart-summary, #CartContainer .ajaxcart__inner')
          ) {
            this.#applyDebounced();
          }
        });
      }
    }

    #observeCartContainers() {
      if (!this.#observer) return;
      const targets = [];
      const drawer = document.getElementById('CartContainer');
      if (drawer) targets.push(drawer);
      document.querySelectorAll('.cart.cart-form').forEach((form) => targets.push(form));
      targets.forEach((el) => this.#observer.observe(el, { childList: true, subtree: true }));
    }

    // --- 选择/切换 ------------------------------------------------------------

    #onGiftSelectChange = (event) => {
      const sel = event.target;
      if (!(sel instanceof HTMLSelectElement) || !sel.hasAttribute(SELECT_ATTR)) return;
      this.#selectedVariantId = String(sel.value);
      try {
        sessionStorage.setItem(SELECTED_STORAGE_KEY, this.#selectedVariantId);
      } catch (error) {
        /* 忽略，仅影响刷新后的默认选中 */
      }
      if (this.#currentTier) {
        this.#pendingGift = this.#chooseGift(this.#currentTier);
        this.#applyDom();
        this.#syncGiftAttribute(); // 用户主动换选，写一次 attribute
      }
    };

    // --- 计算 -----------------------------------------------------------------

    #throttled() {
      return Date.now() < this.#throttledUntil;
    }
    #isExpired() {
      return this.#endTime != null && Date.now() > this.#endTime;
    }
    #matchTier(subtotal) {
      return this.tiers.find((t) => subtotal >= t.min && subtotal <= t.max) || null;
    }
    /** 带 _free_gift 标记的行才是赠品行（与 function/extension 同口径） */
    #hasGiftProperty(item) {
      return Boolean(item && item.properties && item.properties[GIFT_PROPERTY_KEY] != null);
    }
    #qualifyingSubtotal(cart) {
      if (!cart || !Array.isArray(cart.items)) return 0;
      return cart.items.reduce((sum, item) => {
        if (this.#hasGiftProperty(item)) return sum;
        return sum + (item.original_line_price || 0);
      }, 0);
    }
    #availableCandidates(tier) {
      return tier.candidates.filter((c) => c.available);
    }
    #chooseGift(tier) {
      const avail = this.#availableCandidates(tier);
      if (!avail.length) return null;
      return avail.find((c) => c.variantId === this.#selectedVariantId) || avail[0];
    }

    // --- 下拉框 ---------------------------------------------------------------

    #buildSelect(tier, selectedVariantId) {
      const avail = this.#availableCandidates(tier);
      if (avail.length < 2) return null; // 仅一个候选无需切换
      const sel = document.createElement('select');
      sel.setAttribute(SELECT_ATTR, '');
      sel.className = 'free-gift-select';
      avail.forEach((c) => {
        const opt = document.createElement('option');
        opt.value = c.variantId;
        opt.textContent = c.title;
        if (c.variantId === selectedVariantId) opt.selected = true;
        sel.appendChild(opt);
      });
      return sel;
    }

    // --- 虚拟行 ---------------------------------------------------------------

    #removeVirtualRows() {
      document.querySelectorAll(`[${VIRTUAL_ATTR}]`).forEach((row) => row.remove());
    }

    /**
     * 剔除主题渲染出来的「带赠品标记」真实行（API 删除/重渲染未跟上时的显示兜底）。
     * 标记是隐藏 property，DOM 上看不到，用行内 input[data-id]（= line item key）
     * 映射回 #cart.items 判断；不带标记的同款商品行 = 顾客正常购买，不动。
     */
    #removeRealGiftRows() {
      const items = (this.#cart && this.#cart.items) || [];
      const giftKeys = new Set(items.filter((i) => this.#hasGiftProperty(i)).map((i) => String(i.key)));
      if (giftKeys.size === 0) return;
      // 购物车页行：.cart__row 内 input[data-id=key]；抽屉行：.ajaxcart__product 内同样有 data-id=key
      document.querySelectorAll('.cart__row, .ajaxcart__product').forEach((row) => {
        if (row.hasAttribute(VIRTUAL_ATTR)) return;
        const keyEl = row.querySelector('[data-id]');
        const key = keyEl && keyEl.getAttribute('data-id');
        if (key && giftKeys.has(String(key))) row.remove();
      });
    }

    #renderVirtual(gift, tier) {
      if (!gift) {
        this.#removeVirtualRows();
        return;
      }
      // 购物车页（.cart-summary 内、汇总区之前）
      document.querySelectorAll('.cart.cart-form .cart-summary').forEach((summary) => {
        const existing = summary.querySelector(`[${VIRTUAL_ATTR}]`);
        const row = this.#buildPageRow(gift, tier);
        if (existing) {
          existing.replaceWith(row);
        } else {
          const anchor = summary.querySelector('.cart-last-box-top');
          if (anchor) summary.insertBefore(row, anchor);
          else summary.appendChild(row);
        }
      });
      // ajax 抽屉
      document.querySelectorAll('#CartContainer .ajaxcart__inner').forEach((inner) => {
        const existing = inner.querySelector(`[${VIRTUAL_ATTR}]`);
        const row = this.#buildDrawerRow(gift, tier);
        if (existing) existing.replaceWith(row);
        else inner.appendChild(row);
      });
    }

    /** 购物车页虚拟行（复刻 main-cart 的 .cart__row 栅格结构） */
    #buildPageRow(gift, tier) {
      const row = document.createElement('div');
      row.setAttribute(VIRTUAL_ATTR, '');
      row.className = 'cart__row';
      const img = gift.image
        ? `<img src="${this.#escAttr(gift.image)}" alt="${this.#escAttr(gift.title)}" loading="lazy">`
        : '';
      const priceLabel = this.#giftPriceLabel(gift);
      const struck = priceLabel ? `<del class="cart__price">${this.#escHtml(priceLabel)}</del>` : '';
      row.innerHTML = `
        <div class="grid--full cart__row--table-large">
          <div class="grid__item large--three-fifths">
            <div class="grid">
              <div class="grid__item cart_product_image">
                <span class="cart__image">${img}</span>
              </div>
              <div class="grid__item two-thirds">
                <span class="h4 cart__product-name">${this.#escHtml(gift.title)}</span>
                <p class="cart__product-meta free-gift-label">${this.#escHtml(this.giftLabel)}</p>
              </div>
            </div>
          </div>
          <div class="grid__item large--two-fifths cart__pricing">
            <div class="grid--full cart__row--table">
              <div class="grid__item one-third text-center" data-free-gift-qty></div>
              <div class="grid__item two-thirds text-right">${struck}</div>
            </div>
          </div>
        </div>
      `;
      const select = this.#buildSelect(tier, gift.variantId);
      if (select) {
        const cell = row.querySelector('[data-free-gift-qty]');
        if (cell) cell.appendChild(select);
      }
      return row;
    }

    /** 抽屉虚拟行（复刻 ajax-cart-template 的 .ajaxcart__product 结构） */
    #buildDrawerRow(gift, tier) {
      const wrap = document.createElement('div');
      wrap.setAttribute(VIRTUAL_ATTR, '');
      wrap.className = 'ajaxcart__product';
      const img = gift.image
        ? `<img src="${this.#escAttr(gift.image)}" alt="${this.#escAttr(gift.title)}" loading="lazy">`
        : '';
      const priceLabel = this.#giftPriceLabel(gift);
      const struck = priceLabel
        ? `<del class="ajaxcart__price">${this.#escHtml(priceLabel)}</del>`
        : '';
      wrap.innerHTML = `
        <div class="ajaxcart__row">
          <div class="grid">
            <div class="grid__item one-quarter">
              <span class="ajaxcart__product-image">${img}</span>
            </div>
            <div class="grid__item three-quarters">
              <div class="ajaxcart__product-name--wrapper">
                <span class="ajaxcart__product-name">${this.#escHtml(gift.title)}</span>
                <span class="ajaxcart__product-meta free-gift-label">${this.#escHtml(this.giftLabel)}</span>
              </div>
              <div class="grid--full">
                <div class="grid__item one-half" data-free-gift-qty></div>
                <div class="grid__item one-half text-right">${struck}</div>
              </div>
            </div>
          </div>
        </div>
      `;
      const select = this.#buildSelect(tier, gift.variantId);
      if (select) {
        const cell = wrap.querySelector('[data-free-gift-qty]');
        if (cell) cell.appendChild(select);
      }
      return wrap;
    }

    #escHtml(s) {
      return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }
    #escAttr(s) {
      return String(s).replace(/"/g, '&quot;');
    }

    // --- 购物车写操作 ---------------------------------------------------------

    async #safeFetch(url, opts) {
      if (this.#throttled()) return null;
      this.#selfWrite = true;
      let res;
      try {
        res = await fetch(url, opts);
      } catch (error) {
        return null;
      } finally {
        this.#selfWrite = false;
      }
      if (res.status === 429) {
        this.#throttledUntil = Date.now() + THROTTLE_BACKOFF_MS;
        return null;
      }
      return res;
    }

    /** @param {Object[]} lines */
    async #removeLines(lines) {
      let changed = false;
      for (const line of lines) {
        const body = JSON.stringify({ id: line.key, quantity: 0 });
        const res = await this.#safeFetch(this.routes.cartChange, jsonPost(body));
        if (res && res.ok) changed = true;
      }
      if (changed) {
        this.#cart = (await this.#getCart()) || this.#cart;
      }
      return changed;
    }

    /** 我们删行后让主题界面跟上：抽屉重建 + 购物车页重拉 section */
    #refreshThemeCartUI() {
      try {
        if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
          window.ajaxCart.load();
        }
      } catch (error) {
        /* 抽屉刷新失败不致命，observer 仍会维持虚拟行 */
      }
      const form = document.querySelector('.cart.cart-form');
      if (!form) return;
      fetch(`${this.routes.root}?section_id=main-cart`)
        .then((res) => (res.ok ? res.text() : null))
        .then((html) => {
          if (!html) return;
          const holder = document.createElement('div');
          holder.innerHTML = html;
          const incoming = holder.querySelector('.cart.cart-form');
          const current = document.querySelector('.cart.cart-form');
          if (incoming && current) current.innerHTML = incoming.innerHTML;
        })
        .catch(() => {});
    }

    async #getCart() {
      if (this.#throttled()) return null;
      const res = await this.#safeFetch(`${this.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      if (!res) return null;
      return res.json().catch(() => null);
    }
  }

  function boot() {
    try {
      sessionStorage.removeItem('free-gift:product-cache');
    } catch (error) {
      /* ignore */
    }
    const el = document.getElementById('free-gift-config');
    if (!el || !el.textContent) return;
    let config;
    try {
      config = JSON.parse(el.textContent);
    } catch (error) {
      return;
    }
    new FreeGiftManager(config).init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
